import React, { useCallback, useMemo } from 'react';

import { DiscordFillBrand, Globe, TwitterFillBrand } from 'lib/lucky-x-icons-2';
import classNames from 'classnames';
import dayjs from 'dayjs';
import trophyIcon from 'assets/emoji/trophy.png';
import { blockChainIcon } from 'lib/blockChain';

interface IBannerProps {
  isChangeStyle?: boolean;
  isRecommend: boolean;
  banner: string;
  logo: string;
  blockchain: string;
  twitter: string | undefined;
  dcInviteLink: string | undefined;
  website: string | undefined;
  mintDate: number | undefined;
  mintTime: string;
  hasWon?: boolean;
}

const Banner: React.FC<IBannerProps> = ({
  isChangeStyle,
  isRecommend,
  banner,
  logo,
  blockchain,
  twitter,
  dcInviteLink,
  website,
  mintDate,
  mintTime,
  hasWon,
}) => {
  const bannerRender = useCallback(() => {
    if (!!banner) {
      return (
        <div className=" box-border h-full w-full overflow-hidden rounded-xl">
          <img
            src={banner}
            className="h-full w-full rounded-xl object-cover"
            onError={(e: any) => {
              e.target.src =
                'https://s.luckygo.io/luckybuy/img/detailDefaultBanner2.png';
            }}
            alt=""
          />
        </div>
      );
    } else {
      return (
        <div className=" box-border h-full w-full overflow-hidden rounded-xl">
          <img
            src="https://s.luckygo.io/luckybuy/img/detailDefaultBanner2.png"
            className="h-full w-full rounded-xl object-cover"
            alt=""
          />
        </div>
      );
    }
  }, [banner]);

  const logoRender = useCallback(() => {
    if (!!logo) {
      return (
        <img
          src={logo}
          className="h-full w-full rounded-full"
          alt=""
          onError={(e: any) => {
            e.target.src =
              'https://s.luckygo.io/luckybuy/img/detailAvatarImg.png';
          }}
        />
      );
    } else {
      return (
        <img
          src="https://s.luckygo.io/luckybuy/img/detailAvatarImg.png"
          className="h-full w-full rounded-full"
          alt=""
        />
      );
    }
  }, [logo]);

  const socialRender = useCallback(() => {
    return (
      <div className="flex items-center justify-end gap-2">
        {!!twitter && (
          <a
            href={`https://twitter.com/${twitter}`}
            target="_blank"
            rel="noreferrer"
          >
            <div className="flex h-8 w-8 select-none items-center justify-center rounded-full bg-[rgba(255,255,255,0.5)] backdrop-blur hover:bg-[#00A3FF]  hover:opacity-100">
              <TwitterFillBrand size={24} className="text-neutral-100" />
            </div>
          </a>
        )}

        {!!dcInviteLink && (
          <a href={dcInviteLink} target="_blank" rel="noreferrer">
            <div className="flex h-8 w-8  select-none items-center justify-center rounded-full bg-[rgba(255,255,255,0.5)] backdrop-blur hover:bg-[#5865F2]  hover:opacity-100">
              <DiscordFillBrand size={24} className="text-neutral-100" />
            </div>
          </a>
        )}

        {!!website && (
          <a href={website} target="_blank" rel="noreferrer">
            <div className="flex h-8 w-8  select-none items-center justify-center rounded-full bg-[rgba(255,255,255,0.5)] backdrop-blur hover:bg-neutral-400  hover:opacity-100">
              <Globe size={24} className="text-neutral-100" />
            </div>
          </a>
        )}
      </div>
    );
  }, [dcInviteLink, twitter, website]);

  const mintLocalFormat = useMemo(() => {
    if (!!mintDate && !!mintTime) {
      return dayjs(`${dayjs.utc(mintDate).format('YYYY-MM-DD')}T${mintTime}Z`)
        .utc()
        .local()
        .format('MMMM D, YYYY HH:mm:ss');
    } else if (!!mintDate && !mintTime) {
      return dayjs(mintDate).format('MMMM D, YYYY');
    } else {
      return null;
    }
  }, [mintDate, mintTime]);

  const mintingNow = useCallback(() => {
    if (
      (!mintTime && dayjs(mintLocalFormat).isToday()) ||
      (!!mintTime &&
        dayjs(mintLocalFormat).isToday() &&
        dayjs().isSameOrAfter(dayjs(mintLocalFormat, 'MMMM D, YYYY HH:mm:ss')))
    ) {
      return (
        <div className="flex items-center justify-center gap-[2px] rounded-lg bg-[rgba(255,255,255,0.5)] px-2 py-[6px] backdrop-blur">
          <div className="flex h-4 w-4 items-center justify-center">
            <div className="automateBg h-2 w-2 rounded-full"></div>
          </div>
          <div className="text-[10px] font-medium text-neutral-900">
            MINTING NOW
          </div>
        </div>
      );
    }
  }, [mintLocalFormat, mintTime]);

  return (
    <div
      className={classNames('relative', {
        'w-full': isChangeStyle,
        'w-full md:w-[230px] ': !isChangeStyle,
      })}
    >
      <div
        className={classNames(' h-[96px] w-full rounded-xl', {
          'calender-project-card-bg p-[2px]': isRecommend,
        })}
      >
        {bannerRender()}
      </div>
      <div className="projectCardMask absolute bottom-0 left-0 right-0 top-0 rounded-xl">
        <div className=" absolute bottom-2 left-2 h-[60px] w-[60px] rounded-full border-2 border-neutral-100">
          {logoRender()}
          <div className="absolute bottom-0 right-0 flex h-5 w-5 items-center justify-center rounded-full bg-neutral-600">
            <img
              src={blockChainIcon(blockchain)}
              className="h-full w-full rounded-full"
              alt=""
            />
          </div>
        </div>
        <div className="absolute bottom-2 right-2">{socialRender()}</div>
        <div className="absolute right-2 top-2 flex items-center gap-1">
          <div>{mintingNow()}</div>
          {!!hasWon && (
            <div className="flex items-center justify-center gap-[2px] rounded-lg bg-[rgba(255,255,255,0.5)] px-2 py-[6px] backdrop-blur">
              <img src={trophyIcon.src} className="w-4" alt="" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Banner;
